import { Injectable } from '@angular/core';

import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { Store, createSelector } from '@ngrx/store';
import { NumpadDisplayService } from 'packages/sports/web/app/src/numpad/numpad-display.service';
import { Observable, combineLatest, map, of } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { getSystemSlipId } from '../../../bet-generation/services/slip.utils';
import { FreeBetToken } from '../../../reward-tokens/reward-tokens.model';
import { BETSLIP_LINEAR_NUMPAD, IStake } from '../../../stake/models';
import { ILinearContainerStakeService } from '../../../stake/services/digital/linear/linear-container-stake.interface';
import { IStakeErrorViewModel } from '../../../summary/models/stake-errors.model';
import { LinearStakeErrorsService } from '../../../summary/services/linear/linear-stake-errors.service';
import BetslipSystemBetActions from '../../actions';
import { ISystemSlipTypeInfo } from '../../models';
import { systemBetTypeStakeSelectorFactory } from '../../selectors';

const selectIsTypeEachWay = (typeKey: string) =>
    createSelector(systemBetTypeStakeSelectorFactory(typeKey), (typeStake) => typeStake?.isEachWay ?? false);
@Injectable()
export class SystemBetTypeContainerStakeService implements ILinearContainerStakeService {
    private stake$: Observable<IStake | null>;
    private stakeError$: Observable<IStakeErrorViewModel>;

    private isEpcotEnabled: boolean;
    private adornmentText$: Observable<string | undefined>;

    private systemTypeInfo: ISystemSlipTypeInfo;

    constructor(
        epcotConfig: EpcotConfig,
        private numpadDisplayService: NumpadDisplayService,
        private stakeErrorService: LinearStakeErrorsService,
        private store: Store<IBetslipRootState>,
    ) {
        this.isEpcotEnabled = epcotConfig.isEnabled;
    }

    initialize(systemTypeInfo: ISystemSlipTypeInfo): void {
        this.systemTypeInfo = systemTypeInfo;
        this.stake$ = this.store.select(systemBetTypeStakeSelectorFactory(this.systemTypeInfo.slipType.key));

        const errors$ = this.stakeErrorService.getStakeErrors$(getSystemSlipId(this.systemTypeInfo.slipType));

        this.stakeError$ = combineLatest([errors$, this.stake$]).pipe(
            map(([errors, stake]) => {
                const isNumpadOpen = this.numpadDisplayService.isOpen(this.getKeypadId());

                return this.stakeErrorService.convertToViewModel(errors, stake, isNumpadOpen);
            }),
        );

        const betCount = this.systemTypeInfo.betCount;

        this.adornmentText$ = this.isEpcotEnabled
            ? of(undefined)
            : this.store
                  .select(selectIsTypeEachWay(this.systemTypeInfo.slipType.key))
                  .pipe(map((isEachWay) => (isEachWay ? `${betCount * 2}x` : `${betCount}x`)));
    }

    getAdornmentText(): Observable<string | undefined> {
        return this.adornmentText$;
    }

    getKeypadId(): string {
        return BETSLIP_LINEAR_NUMPAD;
    }

    setStake(stakeStr: string, stake: number | null): void {
        this.store.dispatch(
            BetslipSystemBetActions.setSystemBetTypeStake({
                systemType: this.systemTypeInfo.slipType,
                stake,
                silent: false,
                stakeStr,
                betCount: this.systemTypeInfo.betCount,
            }),
        );
    }

    clickStake(stake: IStake | null): void {
        const stakeAmount = stake?.stake ?? null;

        this.store.dispatch(BetslipSystemBetActions.clickSystemTypeStake({ stakeAmount, betType: this.systemTypeInfo.slipType }));
    }

    getStake$(): Observable<IStake | null> {
        return this.stake$;
    }

    getStakeErrors$(): Observable<IStakeErrorViewModel> {
        return this.stakeError$;
    }

    getFreeBetToken$(): Observable<FreeBetToken | null> {
        return of(null);
    }
}
